import classNames from 'classnames';
import moment from 'moment';
import React, { type FC, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../Icon/Icon';
import './Datepicker.css';
import DatePicker, {
  type ReactDatePickerCustomHeaderProps,
} from 'react-datepicker';
import type {
  CalendarType,
  DatepickerProps,
  DayNames,
  Placement,
} from './Datepicker.types';
import 'react-datepicker/dist/react-datepicker.min.css';
import { Button } from '../Button/Button';
import { TextInput } from '../TextInput/TextInput';
import type { TextInputSize } from '../TextInput/TextInput.types';

const dayNamesShort: { [key in DayNames]: string } = {
  Monday: 'Mon',
  Tuesday: 'Tue',
  Wednesday: 'Wed',
  Thursday: 'Thu',
  Friday: 'Fri',
  Saturday: 'Sat',
  Sunday: 'Sun',
};

export const Datepicker: FC<DatepickerProps> = ({
  size = 'default',
  value = null,
  placeholder = 'Choose date',
  ...props
}) => {
  const [startDate, setStartDate] = useState<Date | null>(value);
  const [showCalendarType, setShowCalendarType] =
    useState<CalendarType>('days');
  const ref = React.useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState(
    startDate ? formatDate(startDate) : '',
  );
  const [calendarPlacement, setCalendarPlacement] =
    useState<Placement>('bottom-start');

  const popperContainer = useCallback(
    ({ children }: { children?: React.ReactNode }) => {
      return createPortal(
        <div
          className={classNames(
            'ipf-datepicker-portal',
            `react-datepicker__placement--${calendarPlacement}`,
          )}
        >
          {children}
        </div>,
        document.body,
      );
    },
    [calendarPlacement],
  );

  // Reset the date when the showCalendarType changes to 'years'
  // because the datepicker will not allow to select a year what
  // is already in input field
  useEffect(() => {
    if (showCalendarType === 'years') {
      setStartDate(null);
      setInputValue('');
    }
  }, [showCalendarType]);

  const handleBlur = () => {
    setShowCalendarType('days');
    if (!inputValue) {
      setStartDate(null);
      return;
    }
    // Attempt to parse the date from inputValue
    if (isValidDate(inputValue)) {
      const newDate = new Date(inputValue);
      setInputValue(formatDate(newDate));
      setStartDate(newDate);
    } else {
      setInputValue(startDate ? formatDate(startDate) : '');
    }
    props.onBlur?.();
  };

  const handleChange = (
    date: Date | null,
    event?: React.SyntheticEvent<HTMLElement>,
  ) => {
    if (event && event.target instanceof HTMLInputElement) {
      // change came from input field

      setInputValue(event.target.value);
      setStartDate(date);
    } else {
      // change came from calendar (selected by click or keyboard)

      // do not change the input value if the calendar is in years or months view
      // because the datepicker prevent selecting a year/month that is already in the input field
      if (showCalendarType !== 'years' && showCalendarType !== 'months') {
        setStartDate(date);
        if (date) {
          setInputValue(formatDate(date));
        }
      }
    }
    // focus is sometimes (some combinations) lost after selecting a date
    ref.current?.focus();

    // logic to switch from years to months and from months to days
    // after selection of a year or month
    if (showCalendarType === 'years') {
      setShowCalendarType('months');
    } else {
      setShowCalendarType('days');
    }

    props.onChange?.(date);
  };

  const handleCalendarClose = () => {
    ref.current?.focus();
    setShowCalendarType('days');
  };

  return (
    <div
      className={classNames(
        'ipf-datepicker',
        `react-datepicker__placement--${calendarPlacement}`,
      )}
    >
      <DatePicker
        selected={startDate}
        onChange={handleChange}
        placeholderText={placeholder}
        className={props.className}
        calendarStartDay={1}
        disabled={props.disabled}
        autoFocus={props.autoFocus}
        showYearPicker={showCalendarType === 'years'}
        showMonthYearPicker={showCalendarType === 'months'}
        shouldCloseOnSelect={showCalendarType === 'days'}
        onCalendarClose={handleCalendarClose}
        onBlur={handleBlur}
        name={props.name}
        dateFormat='dd MMM yyyy'
        readOnly={props.readOnly}
        customInput={
          <CustomDatepickerInput
            inputRef={ref}
            invalid={!isValidDate(inputValue)}
            disabled={props.disabled}
            size={size}
            hasError={props.hasError}
            inputId={props.inputId}
          />
        }
        renderCustomHeader={customHeader(showCalendarType, setShowCalendarType)}
        renderMonthContent={renderMonthContent}
        calendarContainer={customCalendarContainer}
        formatWeekDay={formatWeek}
        showPopperArrow={false}
        popperPlacement='bottom-start'
        popperContainer={popperContainer}
        popperModifiers={[
          {
            name: 'sameWidth',
            fn: (state) => {
              if (state.elements?.floating) {
                state.elements.floating.style.width = `${state.rects.reference.width}px`;
              }
              return state;
            },
          },
          {
            name: 'placement',
            fn: (state) => {
              const { placement, y } = state;
              setCalendarPlacement(placement);
              return {
                ...state,
                y: placement.startsWith('bottom') ? y - 11 : y + 11, // condition for auto placement
              };
            },
          },
        ]}
      />
    </div>
  );
};

type CustomDatepickerInputProps = {
  inputRef: React.Ref<HTMLInputElement>;
  hasError?: boolean;
  invalid?: boolean;
  disabled?: boolean;
  size?: TextInputSize;
  inputId?: string;
};

// forwardRef component is required by the react-datepicker
// but we don't need the ref in this case. We pass the ref to the custom input
// thru inputRef prop
const CustomDatepickerInput = React.forwardRef<
  HTMLInputElement,
  CustomDatepickerInputProps
>((props, _ref) => {
  return (
    <TextInput
      value='will ber replaced by props.value'
      type='text'
      endAdornment={
        <Icon
          glyph='fa-calendar'
          className={props.disabled ? '' : 'text-element-secondary'}
        />
      }
      {...props}
      id={props.inputId}
    />
  );
});

const formatWeek = (date: string) => {
  return dayNamesShort[date as DayNames];
};

const validFormats = [
  'DD MMM YYYY',
  'D MMM YYYY',
  'DD MMMM YYYY',
  'D MMMM YYYY',
];

const isValidDate = (dateString: string) => {
  return moment(dateString, validFormats, true).isValid();
};

const formatDate = (date: Date) => {
  const day = String(date.getDate()).padStart(2, '0');
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();

  return `${day} ${month} ${year}`;
};

function customCalendarContainer({
  className,
  children,
}: { className?: string; children: React.ReactNode }) {
  return <div className={`p-xs ${className}`}>{children}</div>;
}

function customHeader(
  showCalendarType: CalendarType,
  setShowCalendarType: (type: CalendarType) => void,
) {
  return (props: ReactDatePickerCustomHeaderProps) => {
    const renderHeader = (props: ReactDatePickerCustomHeaderProps) => {
      const year = props.monthDate.getFullYear();

      if (showCalendarType === 'days') {
        return props.monthDate.toLocaleString('en-US', {
          month: 'long',
          year: 'numeric',
        });
      }
      if (showCalendarType === 'months') {
        return props.monthDate.getFullYear();
      }
      const startYear = Math.floor((year - 1) / 12) * 12 + 1;
      const endYear = startYear + 11;
      return `${startYear} - ${endYear}`;
    };

    return (
      <div className='flex px-3xs justify-between'>
        <div className='flex gap-1 align-middle min-w-[11rem]'>
          <span className='text-level-6 self-center font-semibold'>
            {renderHeader(props)}
          </span>
          <Button
            onClick={() =>
              setShowCalendarType(
                showCalendarType === 'days' ? 'years' : 'days',
              )
            }
            variant='plain'
            icon={showCalendarType === 'days' ? 'fa-angle-down' : 'fa-angle-up'}
          />
        </div>
        <div className='flex'>
          <Button
            onClick={() => {
              if (showCalendarType === 'days') {
                props.decreaseMonth();
              } else if (showCalendarType === 'months') {
                props.decreaseYear();
              } else {
                props.changeYear(props.monthDate.getFullYear() - 10);
              }
            }}
            variant='plain'
            icon='fa-angle-left'
          />
          <Button
            onClick={() => {
              if (showCalendarType === 'days') {
                props.increaseMonth();
              } else if (showCalendarType === 'months') {
                props.increaseYear();
              } else {
                props.changeYear(props.monthDate.getFullYear() + 10);
              }
            }}
            variant='plain'
            icon='fa-angle-right'
          />
        </div>
      </div>
    );
  };
}

const renderMonthContent = (
  _month: number,
  _shortMonth: string,
  longMonth: string,
  _day: Date,
) => {
  return <span>{longMonth}</span>;
};
